import { CreditCard } from 'lucide-react'
import * as React from 'react'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AutopayConsentChoice, type AutopayOffer } from '@/components/AutopayConsentChoice'
import { ManageLinkTurnOffDialog } from '@/components/ManageLinkTurnOffDialog'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import { displayLongDate } from '@/lib/dates'
import { stripePromise } from '@/lib/stripe'

interface ManageLink {
  kind: 'autopay' | 'recurring_gift'
  status: 'active' | 'inactive'
  contact_name: string
  recurring_gift?: {
    amount: string
    amount_cents: number
    frequency: string
    campaign: string
    next_charge_on: string
    status: 'active' | 'paused' | 'disabled' | 'ended'
  } | null
  card: { brand: string; last4: string; expiry: string } | null
  next_charge: {
    amount: string
    amount_cents: number
    charge_on: string
    payment_link_url: string | null
  } | null
  autopay_offer?: {
    eligible: boolean
    reason: string | null
    default_checked: false
    disclosure: AutopayOffer | null
  } | null
}

interface ManageLinkResponse {
  manage_link: ManageLink
}

interface CardSetupResponse {
  card_setup: { id: number; client_secret: string }
}

export function ManageLinkPage({ token }: { token: string }) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['manage-link', token],
    queryFn: () => getJSON<ManageLinkResponse>(`/api/v1/manage_links/${token}`),
    retry: false,
  })
  const turnOff = useMutation({
    mutationFn: () => deleteJSON<ManageLinkResponse>(`/api/v1/manage_links/${token}/autopay`),
    onSuccess: (data) => queryClient.setQueryData(['manage-link', token], data),
  })
  const endGift = useMutation({
    mutationFn: () =>
      deleteJSON<ManageLinkResponse>(`/api/v1/manage_links/${token}/recurring_gift`),
    onSuccess: (data) => queryClient.setQueryData(['manage-link', token], data),
  })
  const manageLink = query.data?.manage_link

  return (
    <main className="min-h-screen bg-background">
      <div className="w-full max-w-[1200px] px-6 py-6">
        <div className="max-w-2xl">
          {query.isLoading ? (
            <p className="text-body-sm text-muted-foreground" role="status">
              Loading Manage Link...
            </p>
          ) : query.isError || !manageLink ? (
            <Alert>Manage Link unavailable</Alert>
          ) : (
            <Card>
              <CardHeader className="p-4 pb-0">
                <CardTitle className="text-heading-sm font-semibold">
                  {manageLink?.kind === 'recurring_gift' ? 'Recurring Gift' : 'Autopay'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4">
                <p className="text-body-sm text-muted-foreground">{manageLink.contact_name}</p>
                {manageLink.kind === 'recurring_gift' && manageLink.recurring_gift ? (
                  <RecurringGiftDetails
                    gift={manageLink.recurring_gift}
                    pending={endGift.isPending}
                    onEnd={() => endGift.mutate()}
                    token={token}
                    onSaved={(data) => queryClient.setQueryData(['manage-link', token], data)}
                  />
                ) : manageLink.kind === 'recurring_gift' ? (
                  <Alert>Recurring Gift unavailable</Alert>
                ) : (
                  <AutopayDetails
                    manageLink={manageLink}
                    token={token}
                    pending={turnOff.isPending}
                    error={turnOff.isError}
                    onTurnOff={() => turnOff.mutate()}
                    onSaved={(data) => queryClient.setQueryData(['manage-link', token], data)}
                  />
                )}
                {endGift.isError && (
                  <Alert>Recurring Gift could not be ended. Please try again.</Alert>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </main>
  )
}

function AutopayDetails({
  manageLink,
  token,
  pending,
  error,
  onTurnOff,
  onSaved,
}: {
  manageLink: ManageLink
  token: string
  pending: boolean
  error: boolean
  onTurnOff: () => void
  onSaved: (data: ManageLinkResponse) => void
}) {
  if (manageLink.status === 'inactive')
    return <InactiveAutopay manageLink={manageLink} token={token} onSaved={onSaved} />

  return (
    <>
      {manageLink.card && (
        <section className="space-y-1" aria-label="Saved Card">
          <h2 className="text-body font-semibold">Saved Card</h2>
          <p className="text-body-sm text-foreground">
            {manageLink.card.brand} ending in {manageLink.card.last4}
          </p>
          <p className="text-caption text-muted-foreground">Expires {manageLink.card.expiry}</p>
        </section>
      )}
      {manageLink.next_charge && (
        <section className="space-y-1" aria-label="Next charge">
          <h2 className="text-body font-semibold">Next charge</h2>
          <p className="text-body-sm tabular-nums text-foreground">
            {manageLink.next_charge.amount} on {formatDate(manageLink.next_charge.charge_on)}
          </p>
          {manageLink.next_charge.payment_link_url && (
            <Button asChild size="sm">
              <a href={manageLink.next_charge.payment_link_url}>
                <CreditCard className="mr-2 h-4 w-4" />
                Pay now
              </a>
            </Button>
          )}
        </section>
      )}
      <section className="space-y-2" aria-label="Update Saved Card">
        <h2 className="text-body font-semibold">Update Saved Card</h2>
        <CardSetup token={token} enroll={false} offer={null} onSaved={onSaved} />
      </section>
      <ManageLinkTurnOffDialog pending={pending} onConfirm={onTurnOff} />
      {error && <Alert>Autopay could not be turned off. Please try again.</Alert>}
    </>
  )
}

function RecurringGiftDetails({
  gift,
  pending,
  onEnd,
  token,
  onSaved,
}: {
  gift: NonNullable<ManageLink['recurring_gift']>
  pending: boolean
  onEnd: () => void
  token: string
  onSaved: (data: ManageLinkResponse) => void
}) {
  const [editingCard, setEditingCard] = React.useState(false)
  const cardFailed = gift.status === 'disabled'
  return (
    <>
      {cardFailed && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted px-4 py-3">
          <Badge variant="warning">Card failed</Badge>
          <p className="min-w-0 flex-1 text-body-sm text-foreground">
            Your gift is stopped until the card is updated.
          </p>
          <Button type="button" size="sm" onClick={() => setEditingCard(true)}>
            Update card
          </Button>
        </div>
      )}
      <section className="space-y-1" aria-label="Recurring Gift">
        <h2 className="text-body font-semibold">Recurring Gift</h2>
        <p className="text-body-sm text-foreground">
          {gift.amount} {gift.frequency} for {gift.campaign}
        </p>
        <p className="text-caption text-muted-foreground">
          Next charge {formatDate(gift.next_charge_on)}
        </p>
      </section>
      {gift.status === 'active' && (
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              End Recurring Gift
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>End Recurring Gift?</DialogTitle>
              <DialogDescription>
                No more charges will be made. Your giving history remains available.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Keep Gift</Button>
              </DialogClose>
              <Button variant="destructive" disabled={pending} onClick={onEnd}>
                End Gift
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {editingCard && (
        <section className="space-y-2" aria-label="Update Saved Card">
          <h2 className="text-body font-semibold">Update Saved Card</h2>
          <CardSetup
            token={token}
            setupPath={`/api/v1/manage_links/${token}/card_setups`}
            enroll={false}
            offer={null}
            onSaved={(data) => {
              onSaved(data)
              setEditingCard(false)
            }}
          />
        </section>
      )}
    </>
  )
}

function InactiveAutopay({
  manageLink,
  token,
  onSaved,
}: {
  manageLink: ManageLink
  token: string
  onSaved: (data: ManageLinkResponse) => void
}) {
  const offer = manageLink.autopay_offer

  return (
    <div className="space-y-4">
      <div className="space-y-2" role="status">
        <Badge variant="neutral">Autopay is off</Badge>
        <p className="text-body-sm text-muted-foreground">
          Your Saved Card has been detached. Your Membership is unchanged.
        </p>
      </div>
      {offer?.eligible && offer.disclosure ? (
        <CardSetup token={token} enroll offer={offer.disclosure} onSaved={onSaved} />
      ) : offer?.reason ? (
        <Alert>{offer.reason}</Alert>
      ) : null}
    </div>
  )
}

function CardSetup({
  token,
  setupPath,
  enroll,
  offer,
  onSaved,
}: {
  token: string
  setupPath?: string
  enroll: boolean
  offer: AutopayOffer | null
  onSaved: (data: ManageLinkResponse) => void
}) {
  if (!stripePromise) return <Alert>Saved Card entry is not configured.</Alert>

  return (
    <Elements stripe={stripePromise}>
      <HostedCardSetup
        token={token}
        setupPath={setupPath}
        enroll={enroll}
        offer={offer}
        onSaved={onSaved}
      />
    </Elements>
  )
}

function HostedCardSetup({
  token,
  setupPath,
  enroll,
  offer,
  onSaved,
}: {
  token: string
  setupPath?: string
  enroll: boolean
  offer: AutopayOffer | null
  onSaved: (data: ManageLinkResponse) => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [consent, setConsent] = React.useState(false)
  const idempotencyKey = React.useRef(cardSetupIdempotencyKey())
  const save = useMutation({
    mutationFn: async () => {
      const card = elements?.getElement(CardElement)
      if (!stripe || !card) throw new Error('Processor-hosted card entry is not ready.')

      const path = setupPath ?? `/api/v1/manage_links/${token}/card_setups`
      const setup = await postJSON<CardSetupResponse>(path, {
        card_setup: { idempotency_key: idempotencyKey.current, consent },
      })
      const confirmation = await stripe.confirmCardSetup(setup.card_setup.client_secret, {
        payment_method: { card },
      })
      if (confirmation.error) {
        throw new Error(confirmation.error.message ?? 'Saved Card could not be submitted.')
      }

      return postJSON<ManageLinkResponse>(`${path}/${setup.card_setup.id}/complete`, {})
    },
    onSuccess: onSaved,
  })

  return (
    <div className="space-y-3">
      {enroll && offer && (
        <AutopayConsentChoice offer={offer} checked={consent} onCheckedChange={setConsent} />
      )}
      <div className="space-y-1.5">
        <p className="text-body-sm font-medium text-foreground">Card details</p>
        <div className="rounded-md border border-border bg-background px-3 py-3">
          <CardElement options={{ hidePostalCode: false }} />
        </div>
        <p className="text-caption text-muted-foreground">
          Card details are securely collected by Stripe. Saving a card does not take a payment.
        </p>
      </div>
      {save.isError && <Alert variant="destructive">{save.error.message}</Alert>}
      <Button
        type="button"
        size="sm"
        disabled={!stripe || save.isPending || (enroll && !consent)}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Saving card...' : enroll ? 'Save card and enroll' : 'Update Saved Card'}
      </Button>
    </div>
  )
}

function cardSetupIdempotencyKey() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()

  return `card-setup-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const formatDate = displayLongDate
